
import axios from "axios";
import { createRoot } from 'react-dom/client';
import { toast } from 'react-toastify';
import SessionExpiredModal from '../Sidebar/Pages/SessionExpiredModal';

const domainURL = " ";
// const domainURL = "https://arthatrack.cloudjiffy.net";
// const domainURL = "https://localhost:1400";

// Always send the auth cookie (bearer-token-perm-internal) with every request.
// Individual calls also pass withCredentials, but setting the global default
// guarantees no request can slip through without credentials — which was
// causing the first request after a redirect to reach Envoy with no cookie,
// get a 401, and falsely trigger the session-expired flow for valid sessions.  
axios.defaults.withCredentials = true;

// ---------------------------------------------------------------------------
// In-flight GET de-duplication
// ---------------------------------------------------------------------------
// Several components mount together on the same screen (e.g. Header + Dashboard
// + Chart) and each fires the same GET on mount; React 18 StrictMode also
// double-invokes effects in development. The result is the same read endpoint
// hitting the network 2-3 times simultaneously.
//
// This wraps axios.get so that while an identical GET (same URL + params) is
// already in flight, callers share that single promise instead of issuing a
// new network request. It is intentionally limited to GET (idempotent reads):
// POST/PUT/DELETE are never de-duplicated, so write behaviour is unchanged.
// Each entry is removed as soon as the request settles, so subsequent (later)
// GETs still hit the network normally — this only collapses concurrent dupes.
const inFlightGets = new Map();
const originalAxiosGet = axios.get.bind(axios);
axios.get = (url, config) => {
    let key;
    try {
        key = url + '::' + JSON.stringify(config?.params || {});
    } catch (e) {
        // Non-serialisable params — skip de-duplication, just pass through.
        return originalAxiosGet(url, config);
    }
    const existing = inFlightGets.get(key);
    if (existing) {
        return existing;
    }
    const promise = originalAxiosGet(url, config).finally(() => {
        inFlightGets.delete(key);
    });
    inFlightGets.set(key, promise);
    return promise;
};

let isShowingSessionExpiredModal = false;

// Bootstrap endpoints that the app fires automatically on mount (before the
// user does anything). A transient 401 on these — e.g. the auth cookie not yet
// attached on the very first cross-origin request after a redirect — must NOT
// eject the user: the callers already fall back to role defaults and retry.
// Genuine expiry is still caught on the next real (user-initiated) request.
const SESSION_NON_FATAL_PATHS = [
    '/artha-track/permissions/my-permissions',
    '/artha-track/permissions/features',
];
const isSessionNonFatalRequest = (config) => {
    const url = config?.url || '';
    return SESSION_NON_FATAL_PATHS.some((p) => url.includes(p));
};

const showSessionExpiredModal = () => {
    if (isShowingSessionExpiredModal) return;

    // Clear storage immediately so that on reload Redux-persist has nothing
    // to rehydrate — prevents contexts from firing API calls with stale auth
    localStorage.clear();
    sessionStorage.clear();
    document.cookie.split(";").forEach((c) => {
        document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });

    const modalDiv = document.createElement('div');
    document.body.appendChild(modalDiv);

    const root = createRoot(modalDiv);

    const handleClose = () => {
        setTimeout(() => {
            root.unmount();
            document.body.removeChild(modalDiv);
            isShowingSessionExpiredModal = false;

            // Force full page reload to reset all in-memory state
            window.location.href = '/';
        }, 1000);
    };

    root.render(
        <SessionExpiredModal
            open={true}
            onClose={handleClose}
        />
    );

    isShowingSessionExpiredModal = true;
};

// Show access denied toast notification
const showAccessDeniedToast = (message) => {
    toast.error(message || 'Access Denied: You do not have permission to perform this action.', {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
    });
};

// Add axios response interceptor
axios.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        if (error.response && error.response.status === 401) {
            // Bootstrap permission/feature calls fire automatically on mount and
            // have their own role-based fallback + retry. Don't show the session
            // modal for them — let the caller handle it so a transient missing
            // cookie on first load can't falsely log the user out.
            if (isSessionNonFatalRequest(error.config)) {
                return Promise.reject(error);
            }
            // If already on the login/registration page, just clear storage silently
            const onAuthPage = ['/', '/registration'].includes(window.location.pathname) ||
                window.location.pathname.startsWith('/login');
            if (onAuthPage) {
                localStorage.clear();
                sessionStorage.clear();
                return new Promise(() => { });
            }
            showSessionExpiredModal();
            return new Promise(() => { });
        }
        // Handle 403 Forbidden - Access Denied
        if (error.response && error.response.status === 403) {
            const message = error.response.data?.message || 'Access Denied: You do not have permission to perform this action.';
            showAccessDeniedToast(message);
        }
        return Promise.reject(error);
    }
);

const loginAPI = async (requestBody) => {
    try {

        const response = await axios.post(domainURL + "/authn/registration",
            requestBody,
            {
                withCredentials: true
            }
        );

        return response.data;
    } catch (error) {
        throw error;
    }
};

const newSessionLoginAPI = async (requestBody) => {
    try {

        const response = await axios.post(domainURL + "/authn/auth-new-session",
            requestBody,
            {
                withCredentials: true
            }
        );

        return response;
    } catch (error) {
        throw error;
    }
};

const existingSessionApplyAPI = async (requestBody) => {
    try {

        const response = await axios.post(domainURL + "/personal-loan/checking-existing-application",
            requestBody,
            {
                withCredentials: true
            }
        );

        return response;
    } catch (error) {
        throw error;
    }
};
const inviteMember = async (payload) => {
    try {

        const response = await axios.post(domainURL + '/artha-track/add-member',
            JSON.stringify(payload),
            {
                withCredentials: true
            }
        );

        return response;
    } catch (error) {
        throw error;
    }
};

const verifyOtpAPI = async (requestBody) => {
    try {

        const response = await axios.post(domainURL +
            "/authn/login-otp-validation",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response;
    } catch (error) {
        throw error;
    }
};


// const fetchMemberApi = async () => {
//     try {

//         const response = await axios.get(domainURL + '/artha-track/fetch-member-list', {
//             withCredentials: true,
//         });
//         return response;
//     } catch (error) {
//         throw error;
//     }
// };

const fetchMemberApi = async (requestBody) => {
    try {

        const response = await axios.post(domainURL +
            "/artha-track/fetch-member-list",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response;
    } catch (error) {
        throw error;
    }
};

const fetchPackageApi = async () => {
    try {

        const response = await axios.get(domainURL + '/artha-track/fetch-package-feature-details', {
            withCredentials: true,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

const registerdOtp = async (requestBody) => {
    try {

        const response = await axios.post(domainURL +
            "/authn/register-otp-validation",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response;
    } catch (error) {
        throw error;
    }
};
const calculatePackage = async (requestBody) => {
    try {

        const response = await axios.post(domainURL +
            "/artha-track/subscription-amount-calculation",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        throw error;
    }
};
const payPlanAmount = async (requestBody) => {
    try {

        const response = await axios.post(domainURL +
            "/artha-track/subscribe-plan",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        throw error;
    }
};

const CheckinApi = async (value, locationIds = [], orgId = null, subOrgId = null) => {
    const payload = {
        pageNumber: 1,
        status: value === 'IN' ? "CHECKIN" : value === 'OUT' ? "CHECKOUT" : value === 'CHECKIN' ? "CHECKIN" : value === 'CHECKOUT' ? "CHECKOUT" : "CHECKIN",
        locationIds: locationIds,
    };
    if (orgId) payload.org_id = orgId;
    if (subOrgId) payload.sub_org_id = subOrgId;

    try {
        const response = await axios.post(
            domainURL + "/artha-track/fetch-users-list", // Use the correct endpoint
            JSON.stringify(payload), // Send the constructed payload
            {
                withCredentials: true,
            }
        );

        return response;
    } catch (error) {
        throw error;
    }
};
const FetchWorkSchedulesData = async (locationId = null, orgId = null) => {
    const payload = {
        entity_type: "work_schedule",
        pagenumber: 1,
        parameter: [
            {
                columnName: "status",
                condition: "=",
                value: "ACTIVE"
            }
        ]
    };

    // Add location filter if provided
    if (locationId) {
        payload.location_id = locationId;
    }

    // Add org_id for Super Admin viewing specific organization
    if (orgId) {
        payload.org_id = orgId;
    }

    try {
        const response = await axios.post(
            domainURL + "/artha-track/view-entity-list",
            JSON.stringify(payload),
            {
                withCredentials: true,
            }
        );

        if (response.data && Array.isArray(response.data.viewListData)) {
            return response.data;
        } else {
            console.error('Expected viewListData array not found in response:', response.data);
            return { viewListData: [] };
        }
    } catch (error) {
        throw error;
    }
};


const FetchTimesheetApi = async (selectedDate, pageNumber = 1, pageSize = 20, roleId = '', currentStatus = '', locationId = null, orgId = null, subOrgId = null) => {
    const payload = {
        date: selectedDate, // Use the selected date passed to the function
        period: "Week",     // Hardcoded period
        pagenumber: pageNumber,
        pagesize: pageSize,
    };

    // Build parameter array for filters
    const parameters = [];

    if (roleId) {
        parameters.push({
            columnName: "role_id",
            condition: "=",
            value: roleId
        });
    }

    if (currentStatus) {
        parameters.push({
            columnName: "current_status",
            condition: "=",
            value: currentStatus
        });
    }

    // Add parameter array to payload if there are any filters
    if (parameters.length > 0) {
        payload.parameter = parameters;
    }

    // Add location filter if provided
    if (locationId) {
        payload.location_id = locationId;
    }

    // Add org_id for Super Admin viewing specific organization
    if (orgId) {
        payload.org_id = orgId;
    }

    // Add sub_org_id for Super Admin/Admin viewing a specific sub-org
    if (subOrgId) {
        payload.sub_org_id = subOrgId;
    }

    try {
        const response = await axios.post(
            domainURL + "/artha-track/timesheet-summary", // Use the correct endpoint
            JSON.stringify(payload), // Send the constructed payload
            {
                withCredentials: true,
            }
        );

        return response;

    } catch (error) {
        console.error('Error fetching timesheet data:', error);
        throw error; // Throw the error to be handled by the caller
    }
};
const FetchTimesheetHoursClickApi = async (selectedDate, userId, orgId = null, subOrgId = null) => {
    const payload = {
        date: selectedDate, // Use the selected date passed to the function
        userId: userId,     // Hardcoded period
    };
    // Super Admin/Admin drilled into an org or sub-org via the header's
    // Global Filter: scope the selected member's time entries to that
    // org/sub-org context (see TimeEntries, which validates this server-side).
    if (orgId) payload.org_id = orgId;
    if (subOrgId) payload.sub_org_id = subOrgId;

    try {
        const response = await axios.post(
            domainURL + "/artha-track/time-entries", // Use the correct endpoint
            JSON.stringify(payload), // Send the constructed payload
            {
                withCredentials: true,
            }
        );

        return response;

    } catch (error) {
        console.error('Error fetching timesheet data:', error);
        throw error; // Throw the error to be handled by the caller
    }
};



const LoginAPi = async (requestBody) => {
    try {

        const response = await axios.post(domainURL +
            "/authn/user-login",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response;
    } catch (error) {
        throw error;
    }
};
const AddWorScheduleApi = async (requestBody) => {
    try {

        const response = await axios.post(domainURL +
            "/artha-track/create-entity",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response;
    } catch (error) {
        throw error;
    }
};

const fetchUsername = async () => {
    try {

        const response = await axios.get(domainURL + '/user/dashboard', {
            withCredentials: true,
        });
        return response;
    } catch (error) {
        throw error;
    }
};
const fetchDashboardData = async (subOrgId = null, orgId = null) => {
    try {
        // Super Admin/Admin drilled into an org or sub-org via the header's
        // Global Filter: scope the dashboard's org/user/location data to
        // that org/sub-org (see FetchDashboardData, which validates this
        // server-side - Super Admin's org_id override, Admin's sub_org_id
        // against the caller's own accessible scope).
        let url = domainURL + '/artha-track/fetch-dashboard-data';
        const params = [];
        if (orgId) params.push(`org_id=${orgId}`);
        if (subOrgId) params.push(`sub_org_id=${subOrgId}`);
        if (params.length > 0) url += '?' + params.join('&');
        const response = await axios.get(url, {
            withCredentials: true,
        });
        return response;
    } catch (error) {
        throw error;
    }
};
const fetcOrganizationData = async (orgId = null) => {
    try {
        // A Super Admin passes the selected org so the backend returns THAT
        // org's details instead of the Super Admin's own JWT org (which made
        // every org show the same data).
        let url = domainURL + '/artha-track/fetch-organization-details';
        if (orgId) url += `?org_id=${orgId}`;
        const response = await axios.get(url, {
            withCredentials: true,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};
const fetchAccountData = async (requestBody) => {
    try {

        const response = await axios.post(domainURL +
            "/artha-track/fetch-subscription-details",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        throw error;
    }
};

const FetchLocationApi = async (requestBody) => {
    try {

        const response = await axios.post(domainURL +
            "/artha-track/view-entity-list",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response;
    } catch (error) {
        throw error;
    }
};
const fetchSupervisors = async (requestBody) => {
    try {

        const response = await axios.post(domainURL +
            "/artha-track/fetch-data",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        throw error;
    }
};

const fetchRoles = async (requestBody) => {
    try {

        const response = await axios.post(domainURL +
            "/artha-track/fetch-data",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        throw error;
    }
};

const signOut = async () => {
    try {

        const response = await axios.get(domainURL + '/authn/sign-out', {
            withCredentials: true,
        });
        return response;
    } catch (error) {
        throw error;
    }
};

const saveLocationAPI = async (requestBody) => {
    try {
        const response = await axios.post(domainURL +
            "/artha-track/create-entity",
            requestBody,
            {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' },
            }
        );

        return response.data;

    } catch (error) {
        console.error("Error saving location:", error);
        throw error;
    }
};

const fetchLocationApi = async (requestBody) => {
    try {
        const response = await axios.post(domainURL +
            "/artha-track/view-entity-list",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response.data;

    } catch (error) {
        console.error("Error fetching address vintage:", error);
    }
};

const fetchLocationForTrackingApi = async (requestBody) => {
    try {
        const response = await axios.post(domainURL +
            "/artha-track/fetch-user-tracking",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response.data;

    } catch (error) {
        console.error("Error fetching address vintage:", error);
    }
};

const fetchAppUsageDataAPI = async (requestBody) => {
    try {
        const response = await axios.post(domainURL +
            "/artha-track/fetch-app-usage-details",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response.data;

    } catch (error) {
        console.error("Error fetching address vintage:", error);
    }
};

const deleteLocationAPI = async (requestBody) => {
    try {
        const response = await axios.post(domainURL +
            "/artha-track/delete-records",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response.data;

    } catch (error) {
        console.error("Error fetching address vintage:", error);
    }
};
const deleteWorkScheduleAPI = async (requestBody) => {
    try {
        const response = await axios.post(domainURL +
            "/artha-track/delete-records",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response.data;

    } catch (error) {
        console.error("Error fetching address vintage:", error);
    }
};

const UpdateWorkScheduleApi = async (requestBody) => {
    try {
        const response = await axios.post(domainURL +
            "/artha-track/update-work-schedule",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response.data;

    } catch (error) {
        console.error("Error fetching address vintage:", error);
    }
};


const updateLocationAPI = async (requestBody) => {
    try {
        const response = await axios.post(domainURL +
            "/artha-track/update-entity",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response.data;

    } catch (error) {
        console.error("Error fetching address vintage:", error);
    }
};

const saveBeaconAPI = async (requestBody) => {
    try {
        const response = await axios.post(domainURL +
            "/artha-track/create-entity",
            requestBody,
            {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' },
            }
        );

        return response.data;

    } catch (error) {
        console.error("Error saving beacon:", error);
        throw error;
    }
};

const fetchBeaconApi = async (requestBody) => {
    try {
        const response = await axios.post(domainURL +
            "/artha-track/view-entity-list",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response.data;

    } catch (error) {
        console.error("Error fetching beacons:", error);
        throw error;
    }
};

const saveBeaconRoomAPI = async (requestBody) => {
    try {
        const response = await axios.post(domainURL +
            "/artha-track/create-entity",
            requestBody,
            {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' },
            }
        );

        return response.data;

    } catch (error) {
        console.error("Error saving beacon room:", error);
        throw error;
    }
};

const fetchBeaconRoomApi = async (requestBody) => {
    try {
        const response = await axios.post(domainURL +
            "/artha-track/view-entity-list",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response.data;

    } catch (error) {
        console.error("Error fetching beacon rooms:", error);
        throw error;
    }
};

const fetchStudentPresenceSessionsAPI = async (requestBody) => {
    try {
        const response = await axios.post(domainURL +
            "/artha-track/student-presence/query",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response.data;

    } catch (error) {
        console.error("Error fetching student presence sessions:", error);
        throw error;
    }
};

// Raw beacon sightings from the last 24h, each with what became of it
// (PENDING / IN_SESSION / DISCARDED) - see FetchStudentSightings in the backend.
const fetchStudentSightingsAPI = async (requestBody) => {
    try {
        const response = await axios.post(domainURL +
            "/artha-track/student-presence/sightings",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response.data;

    } catch (error) {
        console.error("Error fetching student sightings:", error);
        throw error;
    }
};

// Notification bell: the logged-in user's own student stay-break alerts
// (a student left a beacon before its required stay time).
const fetchStayAlertsAPI = async (limit = 50) => {
    try {
        const response = await axios.get(domainURL +
            "/artha-track/student-stay-alerts",
            {
                params: { limit },
                withCredentials: true,
            }
        );

        return response.data;

    } catch (error) {
        console.error("Error fetching stay alerts:", error);
        throw error;
    }
};

// requestBody: { all: true } or { alertIds: [...] }
const markStayAlertsReadAPI = async (requestBody) => {
    try {
        const response = await axios.post(domainURL +
            "/artha-track/student-stay-alerts/mark-read",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response.data;

    } catch (error) {
        console.error("Error marking stay alerts as read:", error);
        throw error;
    }
};

const updateBeaconAPI = async (requestBody) => {
    try {
        const response = await axios.post(domainURL +
            "/artha-track/update-entity",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response.data;

    } catch (error) {
        console.error("Error updating beacon:", error);
        throw error;
    }
};

const deleteBeaconAPI = async (requestBody) => {
    try {
        const response = await axios.post(domainURL +
            "/artha-track/delete-records",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response.data;

    } catch (error) {
        console.error("Error deleting beacon:", error);
        throw error;
    }
};

const AddManualEntryAPI = async (requestBody) => {
    try {
        const response = await axios.post(domainURL +
            "/artha-track/request-for-approval",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response.data;

    } catch (error) {
        console.error("Error fetching address vintage:", error);
    }
};
const FetchApprovalsApi = async (requestBody) => {
    try {
        const response = await axios.post(domainURL +
            "/artha-track/fetch-members-data",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response.data;

    } catch (error) {
        console.error("Error fetching address vintage:", error);
    }
};

const logoutAPI = async () => {
    try {

        const response = await axios.get(domainURL + '/artha-track/authn/sign-out', {
            withCredentials: true,
        });
        return response;
    } catch (error) {
        throw error;
    }
};

const ApproveEntryApi = async (requestBody) => {
    try {
        const response = await axios.post(domainURL +
            "/artha-track/approve-timesheet",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response.data;

    } catch (error) {
        console.error("Error fetching address vintage:", error);
    }
};

const ApproveSingleEntryApi = async (requestBody) => {
    try {
        const response = await axios.post(domainURL +
            "/artha-track/approve-request",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response.data;

    } catch (error) {
        console.error("Error fetching address vintage:", error);
    }
};

const FiltertrackingDataApi = async (requestBody) => {
    try {
        const response = await axios.post(domainURL +
            "/artha-track/retrieve-filtered-worked-time",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response.data;

    } catch (error) {
        console.error("Error fetching address vintage:", error);
    }
};

const FetchAppUsageApi = async (requestBody) => {
    try {
        const response = await axios.post(domainURL +
            "/artha-track/view-entity-list",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response.data;

    } catch (error) {
        console.error("Error fetching address vintage:", error);
    }
};
const SaveEditedEntriesApi = async (requestBody) => {
    try {
        const response = await axios.post(domainURL +
            "/artha-track/update-entity",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response.data;

    } catch (error) {
        console.error("Error fetching address vintage:", error);
    }
};

const FetchWorkAssignment = async (requestBody) => {
    try {

        const response = await axios.post(domainURL +
            "/artha-track/fetch-work-schedule-assignments",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        throw error;
    }
};

const fetchWorkSchedule = async (requestBody) => {
    try {

        const response = await axios.post(domainURL +
            "/artha-track/fetch-data",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        throw error;
    }
};

const saveWorkAssignmentsApi = async (requestBody) => {
    try {

        const response = await axios.post(domainURL +
            "/artha-track/work-schedule-assignment",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        throw error;
    }
};

const generateReport = async (requestBody) => {
    try {

        const response = await axios.post(domainURL +
            "/artha-track/tracking-report",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        throw error;
    }
};

const generateApplicationUsageReport = async (requestBody) => {
    try {

        const response = await axios.post(domainURL +
            "/artha-track/application-usage-report",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        throw error;
    }
};
const generateTimeEntrieseReport = async (requestBody) => {
    try {

        const response = await axios.post(domainURL +
            "/artha-track/timesheet-entries-report",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        throw error;
    }
};

const updateMemberAPI = async (requestBody) => {
    try {
        const response = await axios.post(domainURL +
            "/artha-track/update-entity",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        console.error("Error updating member:", error);
        throw error;
    }
};

const fetchSubscribedFeaturesAPI = async () => {
    try {

        const response = await axios.get(domainURL + '/artha-track/fetch-subscribed-features', {
            withCredentials: true,
        });
        return response;
    } catch (error) {
        throw error;
    }
};

const fetchTransactionDetailsAPI = async (requestBody) => {
    try {

        const response = await axios.post(domainURL + "/artha-track/fetch-transaction-details",
            requestBody,
            {
                withCredentials: true
            }
        );

        return response.data;
    } catch (error) {
        throw error;
    }
};

const changeManagerAPI = async (requestBody) => {
    try {
        const response = await axios.post(domainURL + "/artha-track/change-manager",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        console.error("Error changing manager:", error);
        throw error;
    }
};

const fetchUnassignedUsersAPI = async (orgId = null) => {
    try {
        // Super Admin viewing a manager in a different org (e.g. Users tab ->
        // manager details -> Add Team Member) must pass that manager's org_id
        // so the backend targets the right org instead of the Super Admin's own.
        let url = domainURL + "/artha-track/fetch-unassigned-users";
        if (orgId) url += `?org_id=${orgId}`;
        const response = await axios.get(url,
            {
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        console.error("Error fetching unassigned users:", error);
        throw error;
    }
};

const assignMembersAPI = async (requestBody) => {
    try {
        const response = await axios.post(domainURL + "/artha-track/assign-members",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        console.error("Error assigning members:", error);
        throw error;
    }
};

const fetchMemberDetails = async (requestBody) => {
    try {
        const response = await axios.post(domainURL + "/artha-track/fetch-member-details",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        console.error("Error fetching member details:", error);
        throw error;
    }
};

const workAttachmentProof = async (requestBody) => {
    try {
        const response = await axios.post(domainURL + "/artha-track/work-proof-attachments",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        console.error("Error fetching member details:", error);
        throw error;
    }
};

const inActiveUser = async (requestBody) => {
    try {
        const response = await axios.post(domainURL + "/artha-track/inactive-user",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        console.error("Inactive users:", error);
        throw error;
    }
};

const attendanceReport = async (requestBody) => {
    try {
        const response = await axios.post(domainURL + "/artha-track/attendance-report",
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );

        return response.data;
    } catch (error) {
        console.error("Generate attendance report:", error);
        throw error;
    }
};

const fetchOrganizationUsers = async (locationId = null, orgId = null, subOrgId = null) => {
    try {
        let url = domainURL + '/artha-track/fetch-organization-users';
        const params = [];
        if (locationId) params.push(`location_id=${locationId}`);
        if (orgId) params.push(`org_id=${orgId}`);
        if (subOrgId) params.push(`sub_org_id=${subOrgId}`);
        if (params.length) url += `?${params.join('&')}`;
        console.log('Fetching organization users from:', url);
        const response = await axios.get(url, {
            withCredentials: true,
        });
        console.log('fetchOrganizationUsers raw response:', response);
        console.log('fetchOrganizationUsers response.data:', response.data);
        return response.data;
    } catch (error) {
        console.error("Error fetching organization users:", error);
        console.error("Error response:", error.response);
        console.error("Error status:", error.response?.status);
        console.error("Error data:", error.response?.data);
        throw error;
    }
};

// =====================================================
// DASHBOARD ENHANCEMENT API FUNCTIONS
// =====================================================

const fetchDashboardSummary = async (locationId = null, orgId = null, subOrgId = null) => {
    try {
        let url = domainURL + '/artha-track/dashboard-summary';
        const params = [];
        if (locationId) {
            params.push(`location_id=${locationId}`);
        }
        if (orgId) {
            params.push(`org_id=${orgId}`);
        }
        if (subOrgId) {
            params.push(`sub_org_id=${subOrgId}`);
        }
        if (params.length > 0) {
            url += '?' + params.join('&');
        }
        const response = await axios.get(url, {
            withCredentials: true,
        });
        return response.data;
    } catch (error) {
        console.error("Error fetching dashboard summary:", error);
        throw error;
    }
};

const FetchMemberTimesheetApi = async (selectedDate, userId) => {
  const payload = {
    date: selectedDate,
    period: "Week",
    userId: userId,
  };

  try {
    const response = await axios.post(
      domainURL + "/artha-track/member-timesheet",
      JSON.stringify(payload),
      {
        withCredentials: true,
      }
    );

    return response;
  } catch (error) {
    console.error("Error fetching member timesheet data:", error);
    throw error;
  }
};

const fetchTeamStatusGrid = async (requestBody) => {
    try {
        const response = await axios.post(domainURL + '/artha-track/team-status-grid',
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );
        return response.data;
    } catch (error) {
        console.error("Error fetching team status:", error);
        throw error;
    }
};

const fetchLocationHeatmap = async (requestBody) => {
    try {
        const response = await axios.post(domainURL + '/artha-track/location-heatmap',
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );
        return response.data;
    } catch (error) {
        console.error("Error fetching location heatmap:", error);
        throw error;
    }
};

const fetchAttendanceTrends = async (requestBody) => {
    try {
        const response = await axios.post(domainURL + '/artha-track/attendance-trends',
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );
        return response.data;
    } catch (error) {
        console.error("Error fetching attendance trends:", error);
        throw error;
    }
};

const fetchGeofenceViolations = async (requestBody) => {
    try {
        const response = await axios.post(domainURL + '/artha-track/geofence-violations',
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );
        return response.data;
    } catch (error) {
        console.error("Error fetching geofence violations:", error);
        throw error;
    }
};

const fetchPendingApprovalsSummary = async (locationId = null, orgId = null, subOrgId = null) => {
    try {
        let url = domainURL + '/artha-track/pending-approvals-summary';
        const params = [];
        if (locationId) {
            params.push(`location_id=${locationId}`);
        }
        if (orgId) {
            params.push(`org_id=${orgId}`);
        }
        if (subOrgId) {
            params.push(`sub_org_id=${subOrgId}`);
        }
        if (params.length > 0) {
            url += '?' + params.join('&');
        }
        const response = await axios.get(url, {
            withCredentials: true,
        });
        return response.data;
    } catch (error) {
        console.error("Error fetching pending approvals:", error);
        throw error;
    }
};

const fetchUserLocationTrail = async (requestBody) => {
    try {
        const response = await axios.post(domainURL + '/artha-track/user-location-trail',
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );
        return response.data;
    } catch (error) {
        console.error("Error fetching location trail:", error);
        throw error;
    }
};

const fetchDashboardPreferences = async () => {
    try {
        const response = await axios.get(domainURL + '/artha-track/dashboard-preferences', {
            withCredentials: true,
        });
        return response.data;
    } catch (error) {
        console.error("Error fetching dashboard preferences:", error);
        throw error;
    }
};

const updateDashboardPreferences = async (requestBody) => {
    try {
        const response = await axios.put(domainURL + '/artha-track/dashboard-preferences',
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );
        return response.data;
    } catch (error) {
        console.error("Error updating dashboard preferences:", error);
        throw error;
    }
};

const fetchHoursDistribution = async (requestBody) => {
    try {
        const response = await axios.post(domainURL + '/artha-track/hours-distribution',
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );
        return response.data;
    } catch (error) {
        console.error("Error fetching hours distribution:", error);
        throw error;
    }
};

const resolveGeofenceViolation = async (requestBody) => {
    try {
        const response = await axios.post(domainURL + '/artha-track/resolve-geofence-violation',
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );
        return response.data;
    } catch (error) {
        console.error("Error resolving geofence violation:", error);
        throw error;
    }
};

const fetchSampleDataMode = async () => {
    try {
        const response = await axios.get(domainURL + '/artha-track/sample-data-mode', {
            withCredentials: true,
        });
        return response.data;
    } catch (error) {
        console.error("Error fetching sample data mode:", error);
        throw error;
    }
};

const fetchDashboardLocations = async () => {
    try {
        const requestBody = {
            entity_type: "location",
            pagenumber: 1,
            parameter: [
                {
                    columnName: "status",
                    condition: "=",
                    value: "ACTIVE"
                }
            ]
        };
        const response = await axios.post(domainURL + '/artha-track/view-entity-list',
            JSON.stringify(requestBody),
            {
                withCredentials: true,
            }
        );
        return response.data;
    } catch (error) {
        console.error("Error fetching dashboard locations:", error);
        throw error;
    }
};

const toggleSampleDataMode = async (enabled) => {
    try {
        const response = await axios.post(domainURL + '/artha-track/sample-data-mode',
            JSON.stringify({ enabled }),
            {
                withCredentials: true,
            }
        );
        return response.data;
    } catch (error) {
        console.error("Error toggling sample data mode:", error);
        throw error;
    }
};

// ==================== SUPER ORG_ADMIN APIs ====================

const fetchSuperAdminStats = async () => {
    try {
        const response = await axios.get(domainURL + '/artha-track/super-admin/dashboard/stats', {
            withCredentials: true,
        });
        return response;
    } catch (error) {
        console.error("Error fetching super admin stats:", error);
        throw error;
    }
};

const fetchSubscriptionDistribution = async () => {
    try {
        const response = await axios.get(domainURL + '/artha-track/super-admin/dashboard/subscription-distribution', {
            withCredentials: true,
        });
        return response;
    } catch (error) {
        console.error("Error fetching subscription distribution:", error);
        throw error;
    }
};

const fetchOrgGrowth = async () => {
    try {
        const response = await axios.get(domainURL + '/artha-track/super-admin/dashboard/org-growth', {
            withCredentials: true,
        });
        return response;
    } catch (error) {
        console.error("Error fetching org growth:", error);
        throw error;
    }
};

const fetchSystemHealth = async () => {
    try {
        const response = await axios.get(domainURL + '/artha-track/super-admin/dashboard/system-health', {
            withCredentials: true,
        });
        return response;
    } catch (error) {
        console.error("Error fetching system health:", error);
        throw error;
    }
};

const fetchRecentActions = async () => {
    try {
        const response = await axios.get(domainURL + '/artha-track/super-admin/dashboard/recent-actions', {
            withCredentials: true,
        });
        return response;
    } catch (error) {
        console.error("Error fetching recent actions:", error);
        throw error;
    }
};

const fetchSuperAdminOrganizations = async (filters) => {
    try {
        const response = await axios.post(domainURL + '/artha-track/super-admin/organizations',
            JSON.stringify(filters),
            {
                withCredentials: true,
            }
        );
        return response;
    } catch (error) {
        console.error("Error fetching organizations:", error);
        throw error;
    }
};

const fetchOrganizationDetails = async (orgId) => {
    try {
        const response = await axios.get(domainURL + `/artha-track/super-admin/organizations/${orgId}`, {
            withCredentials: true,
        });
        return response;
    } catch (error) {
        console.error("Error fetching organization details:", error);
        throw error;
    }
};

const suspendOrganization = async (orgId, action, reason) => {
    try {
        const response = await axios.post(domainURL + `/artha-track/super-admin/organizations/${orgId}/suspend`,
            JSON.stringify({ action, reason }),
            {
                withCredentials: true,
            }
        );
        return response;
    } catch (error) {
        console.error("Error suspending/activating organization:", error);
        throw error;
    }
};

const updateOrganization = async (orgId, data) => {
    try {
        const response = await axios.post(domainURL + `/artha-track/super-admin/organizations/${orgId}/update`,
            JSON.stringify(data),
            {
                withCredentials: true,
            }
        );
        return response;
    } catch (error) {
        console.error("Error updating organization:", error);
        throw error;
    }
};

const fetchSubscriptionPlans = async () => {
    try {
        const response = await axios.get(domainURL + '/artha-track/super-admin/plans', {
            withCredentials: true,
        });
        return response;
    } catch (error) {
        console.error("Error fetching subscription plans:", error);
        throw error;
    }
};

const createPlan = async (data) => {
    try {
        const response = await axios.post(domainURL + '/artha-track/super-admin/plans',
            {
                package_name: data.package_name,
                package_type: data.package_type,
                monthly_price_per_user: data.monthly_price_per_user,
                min_users: data.min_users,
                has_face_id: data.has_face_id || false,
                has_advanced_reports: data.has_advanced_reports || false,
                status: data.status || 'ACTIVE'
            },
            {
                withCredentials: true
            }
        );
        return response;
    } catch (error) {
        console.error("Error creating plan:", error);
        throw error;
    }
};

const updatePlan = async (packageId, data) => {
    try {
        const response = await axios.post(domainURL + '/artha-track/super-admin/update-plan',
            {
                package_id: packageId,
                package_name: data.package_name,
                package_type: data.package_type,
                monthly_price_per_user: data.monthly_price_per_user,
                min_users: data.min_users,
                has_face_id: data.has_face_id || false,
                has_advanced_reports: data.has_advanced_reports || false,
                status: data.status || 'ACTIVE'
            },
            {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' },
            }
        );
        return response;
    } catch (error) {
        console.error("Error updating plan:", error);
        throw error;
    }
};

const assignPlanToOrg = async (data) => {
    try {
        const response = await axios.post(domainURL + '/artha-track/super-admin/assign-plan',
            {
                org_id: data.org_id,
                package_id: data.package_id,
                start_date: data.start_date,
                end_date: data.end_date || null,
                max_users: data.max_users || 10,
                daily_charge_per_user: data.daily_charge_per_user || 0
            },
            {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' },
            }
        );
        return response;
    } catch (error) {
        console.error("Error assigning plan:", error);
        throw error;
    }
};

const toggleOrgFeature = async (orgId, featureName, enabled) => {
    try {
        const response = await axios.post(domainURL + '/artha-track/super-admin/toggle-feature',
            JSON.stringify({ org_id: orgId, feature_name: featureName, enabled }),
            {
                withCredentials: true,
            }
        );
        return response;
    } catch (error) {
        console.error("Error toggling feature:", error);
        throw error;
    }
};

const fetchAuditLogs = async (filters) => {
    try {
        const response = await axios.post(domainURL + '/artha-track/super-admin/audit-logs',
            JSON.stringify(filters),
            {
                withCredentials: true,
            }
        );
        return response;
    } catch (error) {
        console.error("Error fetching audit logs:", error);
        throw error;
    }
};

const fetchSuperAdminUsers = async (orgId) => {
    try {
        const params = orgId ? { org_id: orgId } : {};
        const response = await axios.get(domainURL + '/artha-track/super-admin/users', {
            withCredentials: true,
            params,
        });
        return response;
    } catch (error) {
        console.error("Error fetching super admin users:", error);
        throw error;
    }
};

const toggleUserStatus = async (userId, enabled) => {
    try {
        const response = await axios.post(domainURL + `/artha-track/super-admin/users/${userId}/toggle-status`,
            { status: enabled ? 'ACTIVE' : 'INACTIVE' },
            {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' },
            }
        );
        return response;
    } catch (error) {
        console.error("Error toggling user status:", error);
        throw error;
    }
};

const forceLogoutUser = async (userId) => {
    try {
        const response = await axios.post(domainURL + `/artha-track/super-admin/users/${userId}/force-logout`,
            {},
            {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' },
            }
        );
        return response;
    } catch (error) {
        console.error("Error forcing user logout:", error);
        throw error;
    }
};

// Fetch all organizations list for Super Admin dropdown (simple list without pagination)
const fetchAllOrganizationsList = async () => {
    try {
        const response = await axios.get(domainURL + '/artha-track/super-admin/organizations-list', {
            withCredentials: true,
        });
        return response;
    } catch (error) {
        console.error("Error fetching organizations list:", error);
        throw error;
    }
};

// Fetch the nested organization hierarchy for the Super Admin org filter:
// root org -> child organizations -> each org's sub-organizations.
// Returns data as an array of nodes: { org_id, name, type: 'org'|'sub_org', status, children: [...] }
const fetchOrganizationsTree = async () => {
    try {
        const response = await axios.get(domainURL + '/artha-track/super-admin/organizations-tree', {
            withCredentials: true,
        });
        return response;
    } catch (error) {
        console.error("Error fetching organizations tree:", error);
        throw error;
    }
};

// Fetch the nested sub-org hierarchy for the ADMIN header sub-org filter,
// scoped to the caller's own accessible sub-orgs only (no org layer - ADMIN
// always has exactly one org). Returns the same node shape as
// fetchOrganizationsTree's sub_org nodes: { org_id, sub_org_id, name,
// type: 'sub_org', status, children: [...] }
const fetchAdminSubOrgTree = async () => {
    try {
        const response = await axios.get(domainURL + '/artha-track/admin/sub-organizations-tree', {
            withCredentials: true,
        });
        return response;
    } catch (error) {
        console.error("Error fetching admin sub-org tree:", error);
        throw error;
    }
};

const createOrganization = async (data) => {
    try {
        const response = await axios.post(domainURL + '/artha-track/super-admin/organizations/create',
            data,
            {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' },
            }
        );
        return response;
    } catch (error) {
        console.error("Error creating organization:", error);
        throw error;
    }
};

// Fetch locations for a specific organization (for Super Admin)
const fetchOrganizationLocations = async (orgId) => {
    try {
        const response = await axios.get(domainURL + `/artha-track/super-admin/organizations/${orgId}/locations`, {
            withCredentials: true,
        });
        return response;
    } catch (error) {
        console.error("Error fetching organization locations:", error);
        throw error;
    }
};

// =====================================================
// PERMISSION & RBAC API FUNCTIONS
// =====================================================

// Fetch current user's effective permissions
const fetchUserPermissions = async () => {
    try {
        const response = await axios.get(domainURL + '/artha-track/permissions/my-permissions', {
            withCredentials: true,
        });
        return response;
    } catch (error) {
        console.error("Error fetching user permissions:", error);
        throw error;
    }
};

// Check if user has a specific permission
const checkPermission = async (permissionCode, resourceId = null) => {
    try {
        const response = await axios.post(domainURL + '/artha-track/permissions/check',
            JSON.stringify({ permission_code: permissionCode, resource_id: resourceId }),
            { withCredentials: true }
        );
        return response;
    } catch (error) {
        console.error("Error checking permission:", error);
        throw error;
    }
};

// Fetch menu permissions (for sidebar rendering)
const fetchMenuPermissions = async () => {
    try {
        const response = await axios.get(domainURL + '/artha-track/permissions/menu', {
            withCredentials: true,
        });
        return response;
    } catch (error) {
        console.error("Error fetching menu permissions:", error);
        throw error;
    }
};

// Fetch organization's feature flags
const fetchOrgFeatureFlags = async () => {
    try {
        const response = await axios.get(domainURL + '/artha-track/permissions/features', {
            withCredentials: true,
        });
        return response;
    } catch (error) {
        console.error("Error fetching feature flags:", error);
        throw error;
    }
};

// Check if a specific feature is enabled
const checkFeatureEnabled = async (featureCode) => {
    try {
        const response = await axios.get(domainURL + `/artha-track/permissions/features/${featureCode}/check`, {
            withCredentials: true,
        });
        return response;
    } catch (error) {
        console.error("Error checking feature:", error);
        throw error;
    }
};

// Fetch all permissions (admin)
const fetchAllPermissions = async (permissionType = null) => {
    try {
        const url = permissionType
            ? `${domainURL}/artha-track/admin/permissions?type=${permissionType}`
            : `${domainURL}/artha-track/admin/permissions`;
        const response = await axios.get(url, { withCredentials: true });
        return response;
    } catch (error) {
        console.error("Error fetching all permissions:", error);
        throw error;
    }
};

// Fetch all roles with their permissions
const fetchAllRolesWithPermissions = async () => {
    try {
        const response = await axios.get(domainURL + '/artha-track/admin/roles', {
            withCredentials: true,
        });
        return response;
    } catch (error) {
        console.error("Error fetching roles:", error);
        throw error;
    }
};

// Fetch permissions for a specific role
const fetchRolePermissions = async (roleName) => {
    try {
        const response = await axios.get(domainURL + `/artha-track/admin/roles/${roleName}/permissions`, {
            withCredentials: true,
        });
        return response;
    } catch (error) {
        console.error("Error fetching role permissions:", error);
        throw error;
    }
};

// Fetch custom roles for organization
const fetchCustomRoles = async () => {
    try {
        const response = await axios.get(domainURL + '/artha-track/admin/custom-roles', {
            withCredentials: true,
        });
        return response;
    } catch (error) {
        console.error("Error fetching custom roles:", error);
        throw error;
    }
};

// Create a new custom role
const createCustomRole = async (roleData) => {
    try {
        const response = await axios.post(domainURL + '/artha-track/admin/custom-roles',
            JSON.stringify(roleData),
            { withCredentials: true }
        );
        return response;
    } catch (error) {
        console.error("Error creating custom role:", error);
        throw error;
    }
};

// Update a custom role
const updateCustomRole = async (customRoleId, roleData) => {
    try {
        const response = await axios.put(domainURL + `/artha-track/admin/custom-roles/${customRoleId}`,
            JSON.stringify(roleData),
            { withCredentials: true }
        );
        return response;
    } catch (error) {
        console.error("Error updating custom role:", error);
        throw error;
    }
};

// Delete a custom role
const deleteCustomRole = async (customRoleId) => {
    try {
        const response = await axios.delete(domainURL + `/artha-track/admin/custom-roles/${customRoleId}`, {
            withCredentials: true,
        });
        return response;
    } catch (error) {
        console.error("Error deleting custom role:", error);
        throw error;
    }
};

// Fetch custom role permissions
const fetchCustomRolePermissions = async (customRoleId) => {
    try {
        const response = await axios.get(domainURL + `/artha-track/admin/custom-roles/${customRoleId}/permissions`, {
            withCredentials: true,
        });
        return response;
    } catch (error) {
        console.error("Error fetching custom role permissions:", error);
        throw error;
    }
};

// Update custom role permissions
const updateCustomRolePermissions = async (customRoleId, permissions) => {
    try {
        const response = await axios.put(domainURL + `/artha-track/admin/custom-roles/${customRoleId}/permissions`,
            JSON.stringify({ permissions }),
            { withCredentials: true }
        );
        return response;
    } catch (error) {
        console.error("Error updating custom role permissions:", error);
        throw error;
    }
};

// Fetch user permission overrides
const fetchUserOverrides = async (userId) => {
    try {
        const response = await axios.get(domainURL + `/artha-track/admin/users/${userId}/overrides`, {
            withCredentials: true,
        });
        return response;
    } catch (error) {
        console.error("Error fetching user overrides:", error);
        throw error;
    }
};

// Grant a permission override to a user
const grantUserOverride = async (overrideData) => {
    try {
        const response = await axios.post(domainURL + '/artha-track/admin/users/overrides',
            JSON.stringify(overrideData),
            { withCredentials: true }
        );
        return response;
    } catch (error) {
        console.error("Error granting user override:", error);
        throw error;
    }
};

// Revoke a permission override
const revokeUserOverride = async (overrideId) => {
    try {
        const response = await axios.delete(domainURL + `/artha-track/admin/users/overrides/${overrideId}`, {
            withCredentials: true,
        });
        return response;
    } catch (error) {
        console.error("Error revoking user override:", error);
        throw error;
    }
};

// Fetch organization features
const fetchOrgFeatures = async (orgId = null) => {
    try {
        const url = orgId
            ? `${domainURL}/artha-track/admin/org-features/${orgId}`
            : `${domainURL}/artha-track/admin/org-features`;
        const response = await axios.get(url, { withCredentials: true });
        return response;
    } catch (error) {
        console.error("Error fetching org features:", error);
        throw error;
    }
};

// =====================================================
// SUB-ORGANIZATION MANAGEMENT APIs
// =====================================================

// Fetch all sub-organizations
const fetchSubOrganizations = async (status = null, orgId = null) => {
    try {
        let url = `${domainURL}/artha-track/sub-organizations`;
        const params = [];
        if (status) params.push(`status=${status}`);
        if (orgId) params.push(`org_id=${orgId}`);
        if (params.length) url += `?${params.join('&')}`;
        const response = await axios.get(url, { withCredentials: true });
        return response;
    } catch (error) {
        console.error("Error fetching sub-organizations:", error);
        throw error;
    }
};

// Fetch sub-organization dropdown list
const fetchSubOrgDropdown = async (orgId = null) => {
    try {
        let url = `${domainURL}/artha-track/sub-organizations/dropdown`;
        if (orgId) url += `?org_id=${orgId}`;
        const response = await axios.get(url, { withCredentials: true });
        return response;
    } catch (error) {
        console.error("Error fetching sub-org dropdown:", error);
        throw error;
    }
};

// Fetch potential sub-org managers
const fetchSubOrgManagers = async (orgId = null) => {
    try {
        let url = `${domainURL}/artha-track/sub-organizations/managers`;
        if (orgId) url += `?org_id=${orgId}`;
        const response = await axios.get(url, { withCredentials: true });
        return response;
    } catch (error) {
        console.error("Error fetching sub-org managers:", error);
        throw error;
    }
};

// Fetch single sub-organization details
// Fetch a single sub-organization's details.
// orgId is required when a Super Admin is viewing another organization's sub-org —
// without it the backend falls back to the Super Admin's own JWT org and finds nothing.
const fetchSubOrgDetails = async (subOrgId, orgId = null) => {
    try {
        let url = `${domainURL}/artha-track/sub-organizations/${subOrgId}`;
        if (orgId) url += `?org_id=${orgId}`;
        const response = await axios.get(url, { withCredentials: true });
        return response;
    } catch (error) {
        console.error("Error fetching sub-org details:", error);
        throw error;
    }
};

// Fetch locations under a sub-organization
const fetchSubOrgLocations = async (subOrgId) => {
    try {
        const response = await axios.get(
            `${domainURL}/artha-track/sub-organizations/${subOrgId}/locations`,
            { withCredentials: true }
        );
        return response;
    } catch (error) {
        console.error("Error fetching sub-org locations:", error);
        throw error;
    }
};

// Create new sub-organization
const createSubOrganization = async (data) => {
    try {
        const response = await axios.post(
            `${domainURL}/artha-track/sub-organizations`,
            JSON.stringify(data),
            {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json'
                }
            }
        );
        return response;
    } catch (error) {
        console.error("Error creating sub-organization:", error);
        throw error;
    }
};

// Update sub-organization
const updateSubOrganization = async (subOrgId, data) => {
    try {
        const response = await axios.post(
            `${domainURL}/artha-track/sub-organizations/${subOrgId}/update`,
            JSON.stringify(data),
            { withCredentials: true }
        );
        return response;
    } catch (error) {
        console.error("Error updating sub-organization:", error);
        throw error;
    }
};

// Delete sub-organization
const deleteSubOrganization = async (subOrgId, orgId = null) => {
    try {
        let url = `${domainURL}/artha-track/sub-organizations/${subOrgId}`;
        if (orgId) url += `?org_id=${orgId}`;
        const response = await axios.delete(url, { withCredentials: true });
        return response;
    } catch (error) {
        console.error("Error deleting sub-organization:", error);
        throw error;
    }
};

// Assign location to sub-organization
const assignLocationToSubOrg = async (locationId, subOrgId) => {
    try {
        const response = await axios.post(
            `${domainURL}/artha-track/sub-organizations/assign-location`,
            JSON.stringify({ location_id: locationId, sub_org_id: subOrgId }),
            { withCredentials: true }
        );
        return response;
    } catch (error) {
        console.error("Error assigning location to sub-org:", error);
        throw error;
    }
};

// =====================================================
// MULTI-LOCATION_MANAGER SUPPORT APIs
// =====================================================

/**
 * Assign a manager to a user
 * @param {Object} payload - { userId, managerId, isPrimary, assignmentReason }
 */
const assignManagerAPI = async (payload) => {
    try {
        const response = await axios.post(
            domainURL + "/artha-track/assign-manager",
            JSON.stringify(payload),
            { withCredentials: true }
        );
        return response;
    } catch (error) {
        console.error("Error assigning manager:", error);
        throw error;
    }
};

/**
 * Remove a manager assignment from a user
 * @param {Object} payload - { userId, managerId, removeReason }
 */
const removeManagerAPI = async (payload) => {
    try {
        const response = await axios.post(
            domainURL + "/artha-track/remove-manager",
            JSON.stringify(payload),
            { withCredentials: true }
        );
        return response;
    } catch (error) {
        console.error("Error removing manager:", error);
        throw error;
    }
};

/**
 * Set a manager as the primary manager for a user
 * @param {Object} payload - { userId, managerId }
 */
const setPrimaryManagerAPI = async (payload) => {
    try {
        const response = await axios.post(
            domainURL + "/artha-track/set-primary-manager",
            JSON.stringify(payload),
            { withCredentials: true }
        );
        return response;
    } catch (error) {
        console.error("Error setting primary manager:", error);
        throw error;
    }
};

/**
 * Get all managers assigned to a user
 * @param {string} userId - The user ID
 */
const fetchUserManagersAPI = async (userId) => {
    try {
        const response = await axios.get(
            domainURL + `/artha-track/user/${userId}/managers`,
            { withCredentials: true }
        );
        return response;
    } catch (error) {
        console.error("Error fetching user managers:", error);
        throw error;
    }
};

/**
 * Get all users managed by a manager
 * @param {string} managerId - The manager ID
 */
const fetchManagedUsersAPI = async (managerId) => {
    try {
        const response = await axios.get(
            domainURL + `/artha-track/manager/${managerId}/users`,
            { withCredentials: true }
        );
        return response;
    } catch (error) {
        console.error("Error fetching managed users:", error);
        throw error;
    }
};

/**
 * Fetch organization suspension status
 * Returns whether the org is suspended and reason if any
 */
const fetchOrgSuspensionStatus = async () => {
    try {
        const response = await axios.get(
            domainURL + "/artha-track/org-suspension-status",
            { withCredentials: true }
        );
        return response;
    } catch (error) {
        console.error("Error fetching org suspension status:", error);
        throw error;
    }
};

// =====================================================
// SUPER ADMIN ROLE PERMISSION MANAGEMENT API FUNCTIONS
// =====================================================

/**
 * Fetch roles for an organization (system + custom roles)
 */
const fetchOrgRoles = async (orgId) => {
    try {
        const response = await axios.get(
            domainURL + `/artha-track/super-admin/role-permissions/roles/${orgId}`,
            { withCredentials: true }
        );
        return response;
    } catch (error) {
        console.error("Error fetching org roles:", error);
        throw error;
    }
};

/**
 * Fetch all permissions grouped by module
 */
const fetchAllPermissionsForRoles = async () => {
    try {
        const response = await axios.get(
            domainURL + `/artha-track/super-admin/role-permissions/permissions`,
            { withCredentials: true }
        );
        return response;
    } catch (error) {
        console.error("Error fetching all permissions:", error);
        throw error;
    }
};

/**
 * Fetch role-permission mappings for an organization
 */
const fetchRolePermissionMappings = async (orgId, roleName = '') => {
    try {
        let url = domainURL + `/artha-track/super-admin/role-permissions/mappings/${orgId}`;
        if (roleName) {
            url += `?role_name=${encodeURIComponent(roleName)}`;
        }
        const response = await axios.get(url, { withCredentials: true });
        return response;
    } catch (error) {
        console.error("Error fetching role permission mappings:", error);
        throw error;
    }
};

/**
 * Add a permission to a role for an organization
 */
const addRolePermission = async (data) => {
    try {
        const response = await axios.post(
            domainURL + `/artha-track/super-admin/role-permissions/add`,
            JSON.stringify(data),
            { withCredentials: true }
        );
        return response;
    } catch (error) {
        console.error("Error adding role permission:", error);
        throw error;
    }
};

/**
 * Update a role-permission mapping
 */
const updateRolePermission = async (rolePermissionId, data) => {
    try {
        const response = await axios.put(
            domainURL + `/artha-track/super-admin/role-permissions/update/${rolePermissionId}`,
            JSON.stringify(data),
            { withCredentials: true }
        );
        return response;
    } catch (error) {
        console.error("Error updating role permission:", error);
        throw error;
    }
};

/**
 * Delete a role-permission mapping
 */
const deleteRolePermission = async (rolePermissionId) => {
    try {
        const response = await axios.delete(
            domainURL + `/artha-track/super-admin/role-permissions/delete/${rolePermissionId}`,
            { withCredentials: true }
        );
        return response;
    } catch (error) {
        console.error("Error deleting role permission:", error);
        throw error;
    }
};

/**
 * Bulk update role permissions for an organization
 */
const bulkUpdateRolePermissions = async (data) => {
    try {
        const response = await axios.post(
            domainURL + `/artha-track/super-admin/role-permissions/bulk-update`,
            JSON.stringify(data),
            { withCredentials: true }
        );
        return response;
    } catch (error) {
        console.error("Error bulk updating role permissions:", error);
        throw error;
    }
};

// =====================================================
// CHILD ORGANIZATION MANAGEMENT APIs
// Feature disabled — action creators commented out below (except
// changeUserOrganization and fetchAccessibleOrgsAPI, which are shared with
// unrelated features and stay active). Uncomment together with
// Backend/helpers/childOrgManagement.go and the frontend Child Organization
// pages to restore.
// =====================================================

// const fetchChildOrganizationsAPI = async (params = {}) => {
//     try {
//         const response = await axios.get(domainURL + "/artha-track/admin/child-organizations", {
//             params: {
//                 search: params.search || "",
//                 status: params.status || "",
//                 // Super Admin: scope the child-org list to the selected org's hierarchy.
//                 ...(params.org_id ? { org_id: params.org_id } : {}),
//             },
//             withCredentials: true
//         });
//         return response.data;
//     } catch (error) {
//         console.error("Error fetching child organizations:", error);
//         throw error;
//     }
// };

// const fetchChildOrgDetailsAPI = async (orgId) => {
//     try {
//         const response = await axios.get(domainURL + `/artha-track/admin/child-organizations/${orgId}`, {
//             withCredentials: true
//         });
//         return response.data;
//     } catch (error) {
//         console.error("Error fetching child org details:", error);
//         throw error;
//     }
// };

// const createChildOrganizationAPI = async (data) => {
//     try {
//         const response = await axios.post(
//             domainURL + "/artha-track/admin/child-organizations",
//             JSON.stringify(data),
//             { withCredentials: true }
//         );
//         return response.data;
//     } catch (error) {
//         console.error("Error creating child organization:", error);
//         throw error;
//     }
// };

// Revoke a user's access to their current organization and grant it to another
// one (e.g. moving a Manager to cover an org whose Admin is on leave).
// Allowed for SUPER_ADMIN and ADMIN; the backend scopes Org Admins to their own
// org tree.
const changeUserOrganization = async (userId, targetOrgId, reason = "") => {
    try {
        const response = await axios.put(
            domainURL + `/artha-track/admin/users/${userId}/change-organization`,
            { target_org_id: targetOrgId, reason },
            {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' },
            }
        );
        return response.data;
    } catch (error) {
        console.error("Error changing user's organization:", error);
        throw error;
    }
};

const fetchAccessibleOrgsAPI = async () => {
    try {
        const response = await axios.get(domainURL + "/artha-track/admin/accessible-organizations", {
            withCredentials: true
        });
        return response.data;
    } catch (error) {
        console.error("Error fetching accessible organizations:", error);
        throw error;
    }
};

// const fetchPotentialAdminsAPI = async (params = {}) => {
//     try {
//         const response = await axios.get(domainURL + "/artha-track/admin/users/potential-admins", {
//             params: {
//                 targetOrgId: params.targetOrgId || "",
//             },
//             withCredentials: true
//         });
//         return response.data;
//     } catch (error) {
//         console.error("Error fetching potential admins:", error);
//         throw error;
//     }
// };

// const assignChildOrgAdminAPI = async (orgId, data) => {
//     try {
//         const response = await axios.post(
//             domainURL + `/artha-track/admin/child-organizations/${orgId}/assign-admin`,
//             JSON.stringify(data),
//             { withCredentials: true }
//         );
//         return response.data;
//     } catch (error) {
//         console.error("Error assigning child org admin:", error);
//         throw error;
//     }
// };

// const removeChildOrgAdminAPI = async (orgId, data) => {
//     try {
//         const response = await axios.delete(
//             domainURL + `/artha-track/admin/child-organizations/${orgId}/remove-admin`,
//             {
//                 data: JSON.stringify(data),
//                 withCredentials: true
//             }
//         );
//         return response.data;
//     } catch (error) {
//         console.error("Error removing child org admin:", error);
//         throw error;
//     }
// };

// const fetchOrgHierarchyTree = async (orgId = "") => {
//     try {
//         const url = orgId
//             ? domainURL + `/artha-track/admin/child-organizations/${orgId}/tree`
//             : domainURL + "/artha-track/admin/org-hierarchy/tree";
//         const response = await axios.get(url, {
//             withCredentials: true
//         });
//         return response.data;
//     } catch (error) {
//         console.error("Error fetching org hierarchy tree:", error);
//         throw error;
//     }
// };

// =====================================================
// ORGANIZATION AUDIT LOG APIs (For All Users)
// =====================================================

/**
 * Fetch organization audit logs with filters
 * @param {Object} filters - { entityType, userAction, actorId, startDate, endDate, pageNumber, pageSize }
 */
const fetchOrgAuditLogs = async (filters) => {
    try {
        const response = await axios.post(
            domainURL + '/artha-track/fetch-audit-logs',
            JSON.stringify(filters),
            { withCredentials: true }
        );
        return response;
    } catch (error) {
        console.error("Error fetching organization audit logs:", error);
        throw error;
    }
};

/**
 * Fetch available audit log filter options (entity types and user actions)
 */
const fetchAuditLogFilters = async () => {
    try {
        const response = await axios.get(
            domainURL + '/artha-track/audit-log-filters',
            { withCredentials: true }
        );
        return response;
    } catch (error) {
        console.error("Error fetching audit log filters:", error);
        throw error;
    }
};

const fetchAttendanceReportHistoryAPI = async (page = 1, limit = 10) => {
    try {
        const response = await axios.get(domainURL + `/artha-track/attendance-report-history?page=${page}&limit=${limit}`, {
            withCredentials: true,
        });
        return response;
    } catch (error) {
        console.error("Error fetching attendance report history:", error);
        throw error;
    }
};

const getReportFileURL = (reportId, fileType) => {
    return domainURL + `/artha-track/report-file/${reportId}/${fileType}`;
};

const locationWiseAttendanceReportAPI = async (requestBody) => {
    try {
        const response = await axios.post(domainURL + '/artha-track/location-wise-attendance-report',
            JSON.stringify(requestBody),
            { withCredentials: true }
        );
        return response;
    } catch (error) {
        console.error("Error generating location-wise attendance report:", error);
        throw error;
    }
};

const fetchBrandingAPI = async () => {
    try {
        const response = await axios.get(domainURL + '/artha-track/fetch-branding', {
            withCredentials: true,
        });
        return response;
    } catch (error) {
        console.error("Error fetching branding:", error);
        throw error;
    }
};

const saveBrandingAPI = async (brandingData) => {
    try {
        const response = await axios.post(domainURL + '/artha-track/save-branding',
            JSON.stringify(brandingData),
            { withCredentials: true }
        );
        return response;
    } catch (error) {
        console.error("Error saving branding:", error);
        throw error;
    }
};

const fetchAllOrgsForBrandingAPI = async () => {
    try {
        const response = await axios.get(domainURL + '/artha-track/fetch-orgs-for-branding', {
            withCredentials: true,
        });
        return response;
    } catch (error) {
        console.error("Error fetching orgs for branding:", error);
        throw error;
    }
};

const fetchTemplatesAPI = async (orgSlug) => {
    try {
        const url = orgSlug
            ? domainURL + `/artha-track/fetch-templates?slug=${orgSlug}`
            : domainURL + '/artha-track/fetch-templates';
        const response = await axios.get(url, { withCredentials: true });
        return response;
    } catch (error) {
        console.error("Error fetching templates:", error);
        throw error;
    }
};

const saveTemplateAPI = async (templateData) => {
    try {
        const response = await axios.post(domainURL + '/artha-track/save-template',
            JSON.stringify(templateData),
            { withCredentials: true }
        );
        return response;
    } catch (error) {
        console.error("Error saving template:", error);
        throw error;
    }
};

const resetTemplateAPI = async (templateType, orgSlug) => {
    try {
        const url = orgSlug
            ? domainURL + `/artha-track/reset-template/${templateType}?slug=${orgSlug}`
            : domainURL + `/artha-track/reset-template/${templateType}`;
        const response = await axios.delete(url, { withCredentials: true });
        return response;
    } catch (error) {
        console.error("Error resetting template:", error);
        throw error;
    }
};

const generateArthatrackExcelAPI = async (formData) => {
    try {
        const response = await axios.post(domainURL + '/artha-track/generate-arthatrack-excel', formData, {
            withCredentials: true,
            responseType: 'blob',
        });
        return response;
    } catch (error) {
        console.error("Error generating Arthatrack Excel:", error);
        throw error;
    }
};

const fetchLogsAPI = async (filters) => {
    try {
        const response = await axios.post(domainURL + '/artha-track/system-logs', JSON.stringify(filters), {
            withCredentials: true,
        });
        return response?.data;
    } catch (error) {
        console.error("Error fetching system logs:", error);
        throw error;
    }
};

const fetchLogByIdAPI = async (logId) => {
    try {
        const response = await axios.get(domainURL + `/artha-track/system-logs/${logId}`, {
            withCredentials: true,
        });
        return response?.data;
    } catch (error) {
        console.error("Error fetching log by ID:", error);
        throw error;
    }
};

const fetchLogsSummaryAPI = async (filters) => {
    try {
        const response = await axios.post(domainURL + '/artha-track/system-logs/summary', JSON.stringify(filters), {
            withCredentials: true,
        });
        return response?.data;
    } catch (error) {
        console.error("Error fetching logs summary:", error);
        throw error;
    }
};

export {
    // Existing exports
    fetchUnassignedUsersAPI, assignMembersAPI, changeManagerAPI, fetchTransactionDetailsAPI, fetchSubscribedFeaturesAPI, payPlanAmount, calculatePackage, fetcOrganizationData, fetchPackageApi, fetchAccountData, generateTimeEntrieseReport, generateApplicationUsageReport, generateReport, saveWorkAssignmentsApi, FetchWorkAssignment, SaveEditedEntriesApi, FetchAppUsageApi, FiltertrackingDataApi, ApproveSingleEntryApi, ApproveEntryApi, FetchApprovalsApi, AddManualEntryAPI, updateLocationAPI, UpdateWorkScheduleApi, deleteWorkScheduleAPI, deleteLocationAPI, fetchAppUsageDataAPI, logoutAPI, fetchLocationForTrackingApi, fetchLocationApi, FetchWorkSchedulesData, saveLocationAPI, AddWorScheduleApi, FetchLocationApi, inviteMember, fetchMemberApi, FetchTimesheetHoursClickApi, FetchTimesheetApi, fetchDashboardData, CheckinApi, registerdOtp, LoginAPi, fetchUsername, existingSessionApplyAPI, signOut, fetchSupervisors, newSessionLoginAPI, loginAPI, verifyOtpAPI, fetchRoles, fetchWorkSchedule, updateMemberAPI, fetchMemberDetails, workAttachmentProof, inActiveUser, attendanceReport, fetchOrganizationUsers,
    // Dashboard Enhancement exports
    fetchDashboardSummary,
    fetchTeamStatusGrid,
    fetchLocationHeatmap,
    fetchAttendanceTrends,
    fetchGeofenceViolations,
    fetchPendingApprovalsSummary,
    fetchUserLocationTrail,
    fetchDashboardPreferences,
    updateDashboardPreferences,
    fetchHoursDistribution,
    resolveGeofenceViolation,
    fetchSampleDataMode,
    toggleSampleDataMode,
    fetchDashboardLocations,
    // Super Admin exports
    fetchSuperAdminStats,
    fetchSubscriptionDistribution,
    fetchOrgGrowth,
    fetchSystemHealth,
    fetchRecentActions,
    fetchSuperAdminOrganizations,
    fetchOrganizationDetails,
    suspendOrganization,
    updateOrganization,
    createOrganization,
    fetchSubscriptionPlans,
    createPlan,
    updatePlan,
    assignPlanToOrg,
    toggleOrgFeature,
    fetchAuditLogs,
    fetchSuperAdminUsers,
    toggleUserStatus,
    forceLogoutUser,
    fetchAllOrganizationsList,
    fetchOrganizationsTree,
    fetchAdminSubOrgTree,
    fetchOrganizationLocations,
    // Permission & RBAC exports
    fetchUserPermissions,
    checkPermission,
    fetchMenuPermissions,
    fetchOrgFeatureFlags,
    checkFeatureEnabled,
    fetchAllPermissions,
    fetchAllRolesWithPermissions,
    fetchRolePermissions,
    fetchCustomRoles,
    createCustomRole,
    updateCustomRole,
    deleteCustomRole,
    fetchCustomRolePermissions,
    updateCustomRolePermissions,
    fetchUserOverrides,
    grantUserOverride,
    revokeUserOverride,
    fetchOrgFeatures,
    // Sub-Organization Management exports
    fetchSubOrganizations,
    fetchSubOrgDropdown,
    fetchSubOrgManagers,
    fetchSubOrgDetails,
    fetchSubOrgLocations,
    createSubOrganization,
    updateSubOrganization,
    deleteSubOrganization,
    assignLocationToSubOrg,
    // Multi-Manager Support exports
    assignManagerAPI,
    removeManagerAPI,
    setPrimaryManagerAPI,
    fetchUserManagersAPI,
    fetchManagedUsersAPI,
    // Organization Suspension Status
    fetchOrgSuspensionStatus,
    // Super Admin Role Permission Management
    fetchOrgRoles,
    fetchAllPermissionsForRoles,
    fetchRolePermissionMappings,
    addRolePermission,
    updateRolePermission,
    deleteRolePermission,
    bulkUpdateRolePermissions,
    // Child Organization Management — feature disabled, exports commented out
    // (changeUserOrganization and fetchAccessibleOrgsAPI are shared with
    // unrelated features and remain exported).
    // fetchChildOrganizationsAPI,
    // fetchChildOrgDetailsAPI,
    // createChildOrganizationAPI,
    changeUserOrganization,
    fetchAccessibleOrgsAPI,
    // fetchPotentialAdminsAPI,
    // assignChildOrgAdminAPI,
    // removeChildOrgAdminAPI,
    // fetchOrgHierarchyTree,
    // Organization Audit Logs (For All Users)
    fetchOrgAuditLogs,
    fetchAuditLogFilters,
    // Member Timesheet
    FetchMemberTimesheetApi,
    // Arthatrack Excel Report & History
    generateArthatrackExcelAPI,
    fetchAttendanceReportHistoryAPI,
    getReportFileURL,
    // Location-Wise Report
    locationWiseAttendanceReportAPI,
    // Branding
    fetchBrandingAPI,
    saveBrandingAPI,
    fetchAllOrgsForBrandingAPI,
    // Email Templates
    fetchTemplatesAPI,
    saveTemplateAPI,
    resetTemplateAPI,
    // System Logs
    fetchLogsAPI,
    fetchLogByIdAPI,
    fetchLogsSummaryAPI,
    // BLE Beacons
    saveBeaconAPI,
    fetchBeaconApi,
    updateBeaconAPI,
    deleteBeaconAPI,
    saveBeaconRoomAPI,
    fetchBeaconRoomApi,
    fetchStudentPresenceSessionsAPI,
    fetchStayAlertsAPI,
    markStayAlertsReadAPI,
    fetchStudentSightingsAPI,
};


